import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import LogoMark from './LogoMark';
import { businesses } from '../mockData';

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const businessActive = location.pathname.startsWith('/business');

  const navItem = (to, label, testId) => (
    <Link
      to={to}
      data-testid={testId}
      className={`relative text-[13px] font-medium tracking-wide transition-colors focus-ring rounded-sm px-1 py-2 ${
        isActive(to) ? 'text-brand-oxford' : 'text-brand-slate hover:text-brand-oxford'
      }`}
    >
      {label}
      {isActive(to) && (
        <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-brand-oxford" aria-hidden />
      )}
    </Link>
  );

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 bg-brand-alabaster/95 backdrop-blur-[2px] transition-shadow duration-300 ${
        scrolled ? 'border-b border-brand-hairline shadow-ambient' : 'border-b border-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="container-editorial flex items-center justify-between h-20">
        <LogoMark />

        <nav
          aria-label="Primary"
          className="hidden lg:flex items-center gap-8"
        >
          {navItem('/', 'Home', 'nav-home')}

          <div className="relative group">
            <button
              type="button"
              data-testid="nav-businesses"
              aria-haspopup="true"
              className={`text-[13px] font-medium tracking-wide transition-colors focus-ring rounded-sm px-1 py-2 ${
                businessActive ? 'text-brand-oxford' : 'text-brand-slate hover:text-brand-oxford'
              }`}
            >
              Businesses
            </button>
            <div
              role="menu"
              className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white border border-brand-hairline shadow-lift opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
            >
              {businesses.map((b) => (
                <Link
                  key={b.id}
                  to={`/business/${b.id}`}
                  data-testid={`nav-business-${b.id}`}
                  className="block px-5 py-3 border-b border-brand-hairline/60 last:border-0 hover:bg-brand-alabaster transition-colors group/item"
                  role="menuitem"
                >
                  <div
                    className="text-[11px] tracking-[0.16em] uppercase font-bold mb-0.5"
                    style={{ color: `rgb(var(--sub-${b.subBrand}))` }}
                  >
                    {b.tagline.split('·')[0].trim()}
                  </div>
                  <div className="text-sm text-brand-oxford group-hover/item:underline underline-offset-4">
                    {b.name}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {navItem('/about', 'About', 'nav-about')}
          {navItem('/case-studies', 'Case Studies', 'nav-case-studies')}
          {navItem('/insights', 'Insights', 'nav-insights')}
          {navItem('/contact', 'Contact', 'nav-contact')}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="relative inline-flex items-center gap-2 bg-brand-oxford text-brand-alabaster px-5 py-3 text-[12px] font-semibold tracking-[0.14em] uppercase transition-colors hover:bg-brand-gold hover:text-brand-oxford focus-ring"
          >
            Start a Conversation
            <span aria-hidden>→</span>
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-brand-oxford focus-ring"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-brand-hairline bg-white"
          data-testid="mobile-menu"
        >
          <div className="container-editorial py-6 flex flex-col gap-4">
            <Link to="/" className="text-brand-oxford text-sm font-medium py-2" data-testid="m-nav-home">Home</Link>
            <div>
              <div className="eyebrow mb-2">Businesses</div>
              <div className="flex flex-col gap-2 pl-1">
                {businesses.map((b) => (
                  <Link
                    key={b.id}
                    to={`/business/${b.id}`}
                    data-testid={`m-nav-business-${b.id}`}
                    className="text-sm text-brand-slate hover:text-brand-oxford"
                  >
                    {b.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/about" className="text-brand-oxford text-sm font-medium py-2" data-testid="m-nav-about">About</Link>
            <Link to="/case-studies" className="text-brand-oxford text-sm font-medium py-2" data-testid="m-nav-case-studies">Case Studies</Link>
            <Link to="/insights" className="text-brand-oxford text-sm font-medium py-2" data-testid="m-nav-insights">Insights</Link>
            <Link to="/contact" className="text-brand-oxford text-sm font-medium py-2" data-testid="m-nav-contact">Contact</Link>
            <Link
              to="/contact"
              data-testid="m-header-cta"
              className="mt-2 inline-flex items-center justify-center bg-brand-oxford text-brand-alabaster px-5 py-3 text-[12px] font-semibold tracking-[0.14em] uppercase"
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
